import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { EuTaxonomyMetricTypes } from '@upright/common/model/input/eu-taxonomy/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { PaiMetric } from '@upright/common/model/input/pai-assessments/types';
import { SdgAlignmentValence, SdgId, SdgUnitsMode } from '@upright/common/model/output/scores/sdg-alignment/types';
import { HeaderSearchSelection } from '@upright/common/model/search/types';
import { ModelEntityReference } from '@upright/common/model/input/types';
import { Dimension, Valence } from '@upright/common/model/types';
import { RenderViewComponentType } from '@upright/common/renderer/types';
import { GoogleTagManagerEventContext } from './gtm-event-context';

/**
 * Note: max event name length processed on Google's end is 40 characters.
 *
 * TypeScript allows multiple enum declarations with the same name, which are automatically merged.
 * This file uses multiple enum declarations to group related events by feature area for better
 * maintainability and readability. All declarations contribute to a single CustomGtmEvent enum.
 */

/**
 * Global events - Knowledge base, value sets, releases, and front page search
 */
export enum CustomGtmEvent {
  KB_LINK_CLICK = 'upright:kb:link-click',
  VALUE_SET_OPEN = 'upright:vs:open',
  VALUE_SET_PRESET_SELECT = 'upright:vs:preset-select',
  VALUE_SET_DIMENSION_TWEAK = 'upright:vs:dimension-tweak',
  VALUE_SET_CATEGORY_TWEAK = 'upright:vs:category-tweak',
  RELEASE_SELECTOR_OPEN = 'upright:release:selector-open',
  FRONT_PAGE_SEARCH = 'upright:front-page-search',
  RELEASE_CHANGE = 'upright:release:change',
}

/**
 * Screener events - All screener-related interactions
 */
export enum CustomGtmEvent {
  ADVANCED_SCREENER_CLICK = 'upright:screener:advanced-screener-click',
  SCREENER_TABS_CLICK = 'upright:screener:tabs:click',
  SCREENER_FILTERS_ADD = 'upright:screener:filters:add',
  SCREENER_FILTERS_REMOVE = 'upright:screener:filters:remove',
  SCREENER_FILTERS_CLEAR = 'upright:screener:filters:clear',
  SCREENER_COLUMNS_ADD = 'upright:screener:columns:add',
  SCREENER_COLUMNS_REMOVE = 'upright:screener:columns:remove',
  SCREENER_COLUMNS_RESET = 'upright:screener:columns:reset',
  SCREENER_SORT = 'upright:screener:sort',
  SCREENER_PAGE_NEXT = 'upright:screener:page:next',
  SCREENER_PAGE_PREVIOUS = 'upright:screener:page:previous',
  SCREENER_PAGE_FIRST = 'upright:screener:page:first',
  SCREENER_PAGE_LAST = 'upright:screener:page:last',
  SCREENER_COMPANY_CLICK = 'upright:screener:company-click',
}

/**
 * Impact profile events - Company/product profile interactions including NI, SDG, PAI, and EU Taxonomy
 */
export enum CustomGtmEvent {
  IMPACT_PROFILE_SEARCH = 'upright:profile:search',
  IMPACT_PROFILE_LAND = 'upright:profile:land',
  IMPACT_PROFILE_IMPACT_TITLE_CLICK = 'upright:profile:impact-title-click',
  IMPACT_PROFILE_HEADER_EXPAND = 'upright:profile:header-expand',
  IMPACT_PROFILE_HEADER_COLLAPSE = 'upright:profile:header-collapse',
  IMPACT_PROFILE_PRODUCTS_EXPAND = 'upright:profile:products-expand',
  IMPACT_PROFILE_PRODUCTS_COLLAPSE = 'upright:profile:products-collapse',
  IMPACT_PROFILE_DESCRIPTION_EXPAND = 'upright:profile:description-expand',
  IMPACT_PROFILE_DESCRIPTION_COLLAPSE = 'upright:profile:description-collapse',
  IMPACT_PROFILE_DATA_DOWNLOAD_CLICK = 'upright:profile:data-download-click',
  IMPACT_PROFILE_DATA_SHARE_CLICK = 'upright:profile:share-click',
  IMPACT_PROFILE_LENS_SELECT = 'upright:profile:lens-select',
  IMPACT_PROFILE_BAR_CLICK = 'upright:profile:bar-click',
  IMPACT_PROFILE_IMAGE_DOWNLOAD_CLICK = 'upright:profile:image-download-click',
  IMPACT_PROFILE_NI_VIEW_OPTIONS_CLICK = 'upright:profile:ni:view-options-click',
  IMPACT_PROFILE_NI_HIST_BAR_SELECT = 'upright:profile:ni:hist-bar-select',
  IMPACT_PROFILE_NI_HIST_EXAMPLAR_CLICK = 'upright:profile:ni:hist-examplar-click',
  IMPACT_PROFILE_NI_DRILLDOWN_CLICK = 'upright:profile:ni:drilldown-click',
  IMPACT_PROFILE_NI_COMPARISON_ADD = 'upright:profile:ni:comparison-add',
  IMPACT_PROFILE_NI_COMPARISON_REMOVE = 'upright:profile:ni:comparison-remove',
  IMPACT_PROFILE_NI_COMPARISON_CLICK = 'upright:profile:ni:comparison-click',
  // TODO should this be removed?
  IMPACT_PROFILE_NI_COMPARISON_VIEW_CHANGE = 'upright:profile:ni:comparison-view-change',
  IMPACT_PROFILE_NI_GAUGES_SCREENER_CLICK = 'upright:profile:ni:gauges:screener-click',
  IMPACT_PROFILE_SDG_DRILLDOWN_OPEN = 'upright:profile:sdg:drilldown-open',
  IMPACT_PROFILE_SDG_UNIT_SELECT = 'upright:profile:sdg:unit-select',
  IMPACT_PROFILE_PAI_DOWNLOAD = 'upright:profile:pai:download',
  IMPACT_PROFILE_PAI_METRIC_SELECT = 'upright:profile:pai:metric-select',
  IMPACT_PROFILE_PAI_SORT = 'upright:profile:pai:sort',
  IMPACT_PROFILE_PAI_PAGE_NEXT = 'upright:profile:pai:page:next',
  IMPACT_PROFILE_PAI_PAGE_PREVIOUS = 'upright:profile:pai:page:previous',
  IMPACT_PROFILE_PAI_PAGE_FIRST = 'upright:profile:pai:page:first',
  IMPACT_PROFILE_PAI_PAGE_LAST = 'upright:profile:pai:page:last',
  IMPACT_PROFILE_EUT_ACT_CLICK = 'upright:profile:eut:act-click',
  IMPACT_PROFILE_EUT_ENTITY_CLICK = 'upright:profile:eut:entity-click',
  IMPACT_PROFILE_EUT_TYPE_SELECT = 'upright:profile:eut:type-select',
  IMPACT_PROFILE_EUT_SORT = 'upright:profile:eut:sort',
  IMPACT_PROFILE_EUT_PAGE_NEXT = 'upright:profile:eut:page:next',
  IMPACT_PROFILE_EUT_PAGE_PREVIOUS = 'upright:profile:eut:page:previous',
  IMPACT_PROFILE_EUT_PAGE_FIRST = 'upright:profile:eut:page:first',
  IMPACT_PROFILE_EUT_PAGE_LAST = 'upright:profile:eut:page:last',
}

/**
 * Impact profile - Net Impact specific events
 */
export enum CustomGtmEvent {
  IMPACT_PROFILE_NI_UNIT_SELECT = 'upright:profile:ni:unit-select',
  IMPACT_PROFILE_NI_TREE_NET_SCORE_FORMAT_SELECT = 'upright:profile:ni:tree-agg-metric',
  IMPACT_PROFILE_NI_COMPARISON_NET_SCORE_FORMAT_SELECT = 'upright:profile:ni:comparison-agg-metric',
  IMPACT_PROFILE_NI_TIME_TRACKING_NET_SCORE_FORMAT_SELECT = 'upright:profile:ni:time-track-agg-metric',
}

/**
 * Assessment source events - PAI and EU Taxonomy
 */
export enum CustomGtmEvent {
  ASSESSMENT_SOURCE_PAI = 'upright:assessment-source:pai',
  ASSESSMENT_SOURCE_EU_TAXONOMY = 'upright:assessment-source:eu-taxonomy',
}

/**
 * AI chat events (PLT-5741) - per-message feedback control. `IMPRESSION` fires
 * once per assistant message when its feedback icons first scroll into view, so
 * we can compare impressions against clicks and catch low discoverability in
 * the data within a week.
 */
export enum CustomGtmEvent {
  CHAT_FEEDBACK_IMPRESSION = 'upright:chat:feedback:impression',
  CHAT_FEEDBACK_VOTE = 'upright:chat:feedback:vote',
  CHAT_FEEDBACK_REASON_SUBMIT = 'upright:chat:feedback:reason-submit',
  /**
   * PLT-7846: the "Ask Upright to review this" card was drawn, sent or cancelled, and the thumbs-down
   * form's link to it was clicked. Shown against sent and cancelled says whether chat offers it at
   * the right moments.
   */
  CHAT_REVIEW_REQUEST_SHOWN = 'upright:chat:review-request:shown',
  CHAT_REVIEW_REQUEST_SENT = 'upright:chat:review-request:sent',
  CHAT_REVIEW_REQUEST_CANCELLED = 'upright:chat:review-request:cancelled',
  CHAT_REVIEW_REQUEST_FROM_FEEDBACK = 'upright:chat:review-request:from-feedback',
}

/**
 * MCP-connector entry-point events (PLT-5889). The connector is broadly
 * entitled but barely used, so the open/copy/docs funnel is what tells us
 * whether the entry point is the fix. `entryPoint` separates the two
 * placements, which reach largely different user populations.
 */
export enum CustomGtmEvent {
  CHAT_CONNECTOR_OPEN = 'upright:chat:connector:open',
  CHAT_CONNECTOR_URL_COPY = 'upright:chat:connector:url-copy',
  CHAT_CONNECTOR_DOCS_CLICK = 'upright:chat:connector:docs-click',
}

/**
 * User acquisition events - Marketing site forms and interactions
 */
export enum CustomGtmEvent {
  UA_BOOK_COMPANY_DEMO_CLICK = 'upright:ua:book-company-demo-click',
  UA_BOOK_COMPANY_DEMO_SUBMIT = 'upright:ua:book-company-demo-submit',
  UA_BOOK_INVESTOR_DEMO_CLICK = 'upright:ua:book-investor-demo-click',
  UA_BOOK_INVESTOR_DEMO_SUBMIT = 'upright:ua:book-investor-demo-submit',
  UA_WEBINAR_CLICK = 'upright:ua:webinar-form-click',
  UA_WEBINAR_FORM_SUBMIT = 'upright:ua:webinar-form-submit',
  UA_WEBINAR_FORM_INTERACTED = 'upright:ua:webinar-form-interacted',
  UA_DOWNLOAD_FORM_CLICK = 'upright:ua:download-form-click',
  UA_DOWNLOAD_FORM_SUBMIT = 'upright:ua:download-form-submit',
  UA_DOWNLOAD_FORM_INTERACTED = 'upright:ua:download-form-interacted',
  UA_CONTACT_FORM_SUBMIT = 'upright:ua:contact-form-submit',
  UA_CONTACT_FORM_INTERACTED = 'upright:ua:contact-form-interacted',
  UA_INLINE_FORM_VISIBLE = 'upright:ua:inline-form-visible',
  UA_FORM_EMAIL_TYPED = 'upright:ua:form-email-typed',
  UA_FORM_PHONE_REVEAL_CLICKED = 'upright:ua:form-phone-reveal-clicked',
  UA_FORM_EMAIL_ADDRESS_CLICKED = 'upright:ua:form-email-address-clicked',
  UA_BOOK_DEMO_CLICK = 'upright:ua:book-demo-click',
  UA_BOOK_DEMO_SUBMIT = 'upright:ua:book-demo-submit',
  UA_BOOK_DEMO_FORM_INTERACTED = 'upright:ua:book-demo-form-interacted',
  UA_CONTACT_CLICK = 'upright:ua:contact-click',
  UA_HEADER_PLATFORM_LINK_CLICK = 'upright:ua:header-platform-link-click',

  UA_NON_LEAD_CONTACT_CLICK = 'upright:ua:non-lead-contact-click',
  UA_NON_LEAD_CONTACT_FORM_SUBMIT = 'upright:ua:non-lead-contact-form-submit',
  UA_NON_LEAD_CONTACT_FORM_INTERACTED = 'upright:ua:non-lead-contact-form-interacted',
}

/**
 * Product-Led Growth (PLG) events - Onboarding, dashboard, and CSRD DMA interactions
 */
export enum CustomGtmEvent {
  PLG_ACCOUNT_CREATION_STARTED = 'upright:plg:account_creation_started',
  PLG_ACCOUNT_CREATION_COMPLETED = 'upright:plg:account_creation_completed',
  PLG_JOINED_ORGANISATION = 'upright:plg:joined-organisation',
  PLG_CONFIRM_COMPANY_VIEWED = 'upright:plg:confirm_company_viewed',
  PLG_COMPANY_CONFIRMED_PRODUCT_FETCH_STARTED = 'upright:plg:company_prod_fetch_started',
  PLG_PRODUCTS_FETCHED_SUCCESSFULLY = 'upright:plg:products_fetched_success',
  PLG_PRODUCT_MODIFICATION_ONBOARDING_VIEWED = 'upright:plg:prod_mod_onboard_viewed',
  PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_DELETED = 'upright:plg:prod_mod_onb_prod_deleted',
  PLG_PRODUCT_MODIFICATION_ONBOARDING_ALL_PRODUCTS_DELETED = 'upright:plg:prod_mod_onb_all_deleted',
  PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_ADDED = 'upright:plg:prod_mod_onb_prod_added',
  PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_EDITED = 'upright:plg:prod_mod_onb_prod_edited',
  PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_SET_CONFIRMED = 'upright:plg:prod_mod_onb_confirmed',
  PLG_ASSESSMENT_TAKES_LONG_EMAIL_SENT = 'upright:plg:assessment_long_email_sent',
  PLG_ASSESSMENT_COMPLETED_SUCCESFULLY = 'upright:plg:assessment_completed',
  PLG_HOME_DASHBOARD_ACCESS = 'upright:plg:home_dashboard_access',
  PLG_HOME_VIDEO_STARTED = 'upright:plg:home_video_started',
  PLG_HOME_VIDEO_COMPLETED = 'upright:plg:home_video_completed',
  PLG_HOME_VIEW_RESULTS_CLICKED = 'upright:plg:home_view_results_clicked',
  PLG_HOME_DOCUMENTATION_TILE_CLICKED = 'upright:plg:home_docs_tile_clicked',
  PLG_HOME_NEWS_TILE_CLICKED = 'upright:plg:home_news_tile_clicked',
  PLG_MATERIALITY_MATRIX_VIEWED = 'upright:plg:materiality_matrix_viewed',
  PLG_MATERIALITY_MATRIX_IMAGE_DOWNLOADED = 'upright:plg:mat_matrix_img_downloaded',
  PLG_MATERIAL_TOPICS_VIEWED = 'upright:plg:material_topics_viewed',
  PLG_SMP_VIEWED = 'upright:plg:smp_viewed',
  PLG_SMP_IMPACT_CARD_VIEWED = 'upright:plg:smp_impact_card_viewed',
  PLG_SMP_IMPACT_CARD_IMPACT_CALCULATION_TOOLTIP_VIEWED = 'upright:plg:smp_impact_calc_tip_viewed',
  PLG_SMP_IMPACT_CARD_PRODUCT_CALCULATION_TOOLTIP_VIEWED = 'upright:plg:smp_product_calc_tip_viewed',
  PLG_SMP_FINANCIAL_MATERIALITY_TAB_CLICKED = 'upright:plg:smp_fin_mat_tab_clicked',
  PLG_SMP_RISKOPPORTUNITY_CARD_VIEWED = 'upright:plg:smp_risk_opp_card_viewed',
  PLG_QUANTITATIVE_OVERVIEW_VIEWED = 'upright:plg:quantitative_overview_viewed',
  PLG_TAILORING_COMMIT_COPIED_RO = 'upright:plg:tailoring_commit_copied_ro',
  PLG_TAILORING_COMMIT_ADD_RO = 'upright:plg:tailoring_commit_add_ro',
  PLG_TAILORING_COMMIT_COPIED_IMPACT = 'upright:plg:tailoring_commit_copied_impact',
  PLG_TAILORING_COMMIT_ADD_IMPACT = 'upright:plg:tailoring_commit_add_impact',
  PLG_TAILORING_COMMIT_DISABLE_RO = 'upright:plg:tailoring_commit_disable_ro',
  PLG_TAILORING_COMMIT_DISABLE_IMPACT = 'upright:plg:tailoring_commit_disable_impact',
  PLG_TAILORING_COMMIT_EDIT_IRO = 'upright:plg:tailoring_commit_edit_iro',
  PLG_TAILORING_OWN_INSIGHTS_ACCESSED = 'upright:plg:tailoring_own_insights_accessed',
  PLG_TAILORING_ADJUSTED_IMPACT_THRESHOLD_COMMIT = 'upright:plg:tailoring_impact_thresh',
  PLG_TAILORING_ADJUSTED_FINANCIAL_THRESHOLD_COMMIT = 'upright:plg:tailoring_fin_thresh',
  PLG_TAILORING_CHANGE_LOG_VIEWED = 'upright:plg:tailoring_change_log_viewed',
  PLG_TAILORING_DELETE_ALL_CHANGES_COMMIT = 'upright:plg:tailoring_delete_all_commit',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_VIEWED = 'upright:plg:prod_mod_dash_viewed',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_DELETED = 'upright:plg:prod_mod_dash_prod_deleted',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_ALL_PRODUCTS_DELETED = 'upright:plg:prod_mod_dash_all_deleted',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_ADDED = 'upright:plg:prod_mod_dash_prod_added',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_EDITED = 'upright:plg:prod_mod_dash_prod_edited',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_ABANDONED_WITHOUT_RECOMPUTE = 'upright:plg:prod_mod_dash_abandoned',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_STARTED = 'upright:plg:prod_mod_dash_recomp_started',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_COMPLETED_SUCCESSFULLY = 'upright:plg:prod_mod_dash_recomp_success',
  PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_FAILED = 'upright:plg:prod_mod_dash_recomp_failed',
  PLG_SIDE_NAVIGATION_FINANCIAL_EFFECTS_EXPANDED = 'upright:plg:side_nav_fin_effects_exp',
  PLG_SIDE_NAVIGATION_GEOGRAPHY_TOOLTIP_VIEWED = 'upright:plg:side_nav_geography_tip',
  PLG_SIDE_NAVIGATION_DISCLOSURES_TOOLTIP_VIEWED = 'upright:plg:side_nav_disclosures_tip',
  PLG_SIDE_NAVIGATION_FINANCIAL_STATEMENTS_TOOLTIP_VIEWED = 'upright:plg:side_nav_fin_stmt_tip',
  // Agentic site-location input funnel (PLT-5470), emitted from the in-chat
  // `<SiteLocationsBatchCard>`: proposed -> edited -> saved -> published. Event strings stay
  // inside Google's 40-character cap.
  PLG_SITE_LOCATIONS_PROPOSED = 'upright:plg:site_loc_proposed',
  PLG_SITE_LOCATIONS_EDITED = 'upright:plg:site_loc_edited',
  PLG_SITE_LOCATIONS_SAVED = 'upright:plg:site_loc_saved',
  PLG_SITE_LOCATIONS_PUBLISHED = 'upright:plg:site_loc_published',
}

/**
 * Edit company dialog events (P-PLT-198): a custom company's or a supplier's inputs changed after
 * creation, so it can be seen how often companies are corrected and how many edits are abandoned.
 */
export enum CustomGtmEvent {
  CUSTOM_COMPANY_EDIT_OPENED = 'upright:company-edit:opened',
  CUSTOM_COMPANY_EDIT_RECALCULATED = 'upright:company-edit:recalculated',
  CUSTOM_COMPANY_EDIT_DISCARDED = 'upright:company-edit:discarded',
  // After a website change on a company already assessed (PLT-6285): how often identifying its
  // products and sites again is offered, taken and turned down. Inside Google's 40-character cap.
  CUSTOM_COMPANY_EDIT_REIDENTIFY_OFFERED = 'upright:company-edit:reidentify-offer',
  CUSTOM_COMPANY_EDIT_REIDENTIFY_ACCEPTED = 'upright:company-edit:reidentify-accept',
  CUSTOM_COMPANY_EDIT_REIDENTIFY_DECLINED = 'upright:company-edit:reidentify-decline',
}

/** Where the Edit company dialog was opened from. */
export type CustomCompanyEditEntryPoint =
  | 'list-pencil'
  | 'list-count'
  | 'company-page'
  | 'supplier-panel'
  | 'climate-risk';

/** Which kind of custom company the dialog edits: an investor's due-diligence company or a supplier. */
export type CustomCompanyEditKind = 'investor' | 'supplier';

type CustomCompanyEditEventPayload =
  | GenericGtmEventPayload<
      CustomGtmEvent.CUSTOM_COMPANY_EDIT_OPENED,
      { companyId: string; kind: CustomCompanyEditKind; entryPoint: CustomCompanyEditEntryPoint; tab: string }
    >
  | GenericGtmEventPayload<
      | CustomGtmEvent.CUSTOM_COMPANY_EDIT_RECALCULATED
      | CustomGtmEvent.CUSTOM_COMPANY_EDIT_DISCARDED
      | CustomGtmEvent.CUSTOM_COMPANY_EDIT_REIDENTIFY_DECLINED,
      { companyId: string; kind: CustomCompanyEditKind }
    >
  | GenericGtmEventPayload<
      CustomGtmEvent.CUSTOM_COMPANY_EDIT_REIDENTIFY_OFFERED | CustomGtmEvent.CUSTOM_COMPANY_EDIT_REIDENTIFY_ACCEPTED,
      // Offered: what the offer covers. Accepted: what the user chose.
      { companyId: string; kind: CustomCompanyEditKind; products: boolean; sites: boolean }
    >;

/**
 * Trial campaign links (PLT-7200): a signed-in reader followed `/go/climate-trial` or
 * `/go/supplier-trial` and the resolver picked where to send them. One event per arrival, so a
 * campaign's use can be counted by audience and by where it led. Readers who stop at the sign-in
 * page are not counted: the link only resolves once there is a session.
 */
export enum CustomGtmEvent {
  TRIAL_LINK_RESOLVED = 'upright:trial-link:resolved',
}

export type TrialLinkId = 'climate-trial' | 'supplier-trial';

/** Where a trial link sent its reader. */
export type TrialLinkDestination = 'chat' | 'suppliers' | 'trial-company-dialog' | 'home';

type TrialLinkEventPayload = GenericGtmEventPayload<
  CustomGtmEvent.TRIAL_LINK_RESOLVED,
  // `organizationType` is the reader's organization type, or 'unknown' when it could not be read.
  { link: TrialLinkId; organizationType: string; destination: TrialLinkDestination }
>;

type BasicImpactProfileEvent =
  | CustomGtmEvent.IMPACT_PROFILE_HEADER_EXPAND
  | CustomGtmEvent.IMPACT_PROFILE_HEADER_COLLAPSE
  | CustomGtmEvent.IMPACT_PROFILE_PRODUCTS_EXPAND
  | CustomGtmEvent.IMPACT_PROFILE_PRODUCTS_COLLAPSE
  | CustomGtmEvent.IMPACT_PROFILE_DESCRIPTION_EXPAND
  | CustomGtmEvent.IMPACT_PROFILE_DESCRIPTION_COLLAPSE
  | CustomGtmEvent.IMPACT_PROFILE_DATA_DOWNLOAD_CLICK
  | CustomGtmEvent.IMPACT_PROFILE_DATA_SHARE_CLICK;

interface GenericGtmEventPayload<E extends CustomGtmEvent, T> {
  event: E;
  data: T;
  contextOverride?: Partial<GoogleTagManagerEventContext>;
}

interface EmptyGtmEventPayload<E extends CustomGtmEvent> {
  event: E;
  contextOverride?: Partial<GoogleTagManagerEventContext>;
}

export type CustomGtmEventPayload =
  | GlobalEventPayload
  | ScreenerEventPayload
  | ImpactProfileEventPayload
  | AssessmentSourceEventPayload
  | FacadeEventPayload
  | PLGEventPayload
  | ChatEventPayload
  | CustomCompanyEditEventPayload
  | TrialLinkEventPayload;

/**
 * AI chat feedback events (PLT-5741). `rating` distinguishes a 👍 from a 👎
 * on a vote; `reasonChipCount` / `hasReasonText` characterise the follow-up
 * reason a 👎 optionally carries (no free-text content is sent to analytics).
 */
type ChatEventPayload =
  | EmptyGtmEventPayload<CustomGtmEvent.CHAT_FEEDBACK_IMPRESSION>
  | GenericGtmEventPayload<CustomGtmEvent.CHAT_FEEDBACK_VOTE, { rating: 'positive' | 'negative' | 'cleared' }>
  | GenericGtmEventPayload<
      CustomGtmEvent.CHAT_FEEDBACK_REASON_SUBMIT,
      { reasonChipCount: number; hasReasonText: boolean }
    >
  | EmptyGtmEventPayload<
      | CustomGtmEvent.CHAT_REVIEW_REQUEST_SHOWN
      | CustomGtmEvent.CHAT_REVIEW_REQUEST_SENT
      | CustomGtmEvent.CHAT_REVIEW_REQUEST_CANCELLED
      | CustomGtmEvent.CHAT_REVIEW_REQUEST_FROM_FEEDBACK
    >
  | GenericGtmEventPayload<
      | CustomGtmEvent.CHAT_CONNECTOR_OPEN
      | CustomGtmEvent.CHAT_CONNECTOR_URL_COPY
      | CustomGtmEvent.CHAT_CONNECTOR_DOCS_CLICK,
      { entryPoint: ChatConnectorEntryPoint }
    >;

/**
 * Which connector entry point an event came from — the pinned footer of the
 * full-page `/chat` history sidebar, or the "Ask AI" side panel's empty state.
 */
export type ChatConnectorEntryPoint = 'chat-sidebar' | 'chat-side-panel';

type GlobalEventPayload =
  | GenericGtmEventPayload<CustomGtmEvent.KB_LINK_CLICK, { url: string }>
  | EmptyGtmEventPayload<CustomGtmEvent.RELEASE_SELECTOR_OPEN>
  | GenericGtmEventPayload<CustomGtmEvent.RELEASE_CHANGE, { selectedRelease: string }>
  | EmptyGtmEventPayload<CustomGtmEvent.VALUE_SET_OPEN>
  | GenericGtmEventPayload<CustomGtmEvent.VALUE_SET_PRESET_SELECT, { preset: string }>
  | GenericGtmEventPayload<
      CustomGtmEvent.VALUE_SET_CATEGORY_TWEAK,
      { impactCode: ImpactCategoryId; valence: Valence; weight: number }
    >
  | GenericGtmEventPayload<
      CustomGtmEvent.VALUE_SET_DIMENSION_TWEAK,
      { dimension: Dimension; valence: Valence; weight: number }
    >
  | EmptyGtmEventPayload<CustomGtmEvent.FRONT_PAGE_SEARCH>;

type ScreenerEventPayload =
  | EmptyGtmEventPayload<CustomGtmEvent.ADVANCED_SCREENER_CLICK>
  | GenericGtmEventPayload<CustomGtmEvent.SCREENER_TABS_CLICK, { screener: 'company' | 'fund' | 'fund-constituent' }>
  | GenericGtmEventPayload<CustomGtmEvent.SCREENER_FILTERS_ADD, { dimension: string; value: any }>
  | GenericGtmEventPayload<CustomGtmEvent.SCREENER_FILTERS_REMOVE, { dimension: string }>
  | EmptyGtmEventPayload<CustomGtmEvent.SCREENER_FILTERS_CLEAR>
  | GenericGtmEventPayload<CustomGtmEvent.SCREENER_COLUMNS_ADD, { dimension: string }>
  | GenericGtmEventPayload<CustomGtmEvent.SCREENER_COLUMNS_REMOVE, { dimension: string }>
  | EmptyGtmEventPayload<CustomGtmEvent.SCREENER_COLUMNS_RESET>
  | GenericGtmEventPayload<CustomGtmEvent.SCREENER_SORT, { columnId: string; desc: boolean }>
  | GenericGtmEventPayload<
      | CustomGtmEvent.SCREENER_PAGE_FIRST
      | CustomGtmEvent.SCREENER_PAGE_PREVIOUS
      | CustomGtmEvent.SCREENER_PAGE_NEXT
      | CustomGtmEvent.SCREENER_PAGE_LAST,
      { destinationPage: number; lander?: boolean }
    >
  | GenericGtmEventPayload<
      CustomGtmEvent.SCREENER_COMPANY_CLICK,
      { companyId: string; companyName: string; lander?: boolean }
    >;

type ImpactProfileEventPayload =
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_SEARCH,
      { search: string; entityRef: ModelEntityReference | undefined; searchType: HeaderSearchSelection | undefined }
    >
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_LAND, { lens: string }>
  | EmptyGtmEventPayload<BasicImpactProfileEvent>
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_IMPACT_TITLE_CLICK,
      { impactCode: ImpactCategoryId; lensType: ImpactLensType }
    >
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_LENS_SELECT, { lens: string }>
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_IMAGE_DOWNLOAD_CLICK, { type?: RenderViewComponentType }>
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_NI_UNIT_SELECT, { selectedUnit: string }>
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_NI_TREE_NET_SCORE_FORMAT_SELECT, { selectedFormat: string }>
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_NI_COMPARISON_NET_SCORE_FORMAT_SELECT,
      { selectedFormat: string }
    >
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_NI_TIME_TRACKING_NET_SCORE_FORMAT_SELECT,
      { selectedFormat: string }
    >
  | EmptyGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_NI_VIEW_OPTIONS_CLICK>
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_NI_HIST_BAR_SELECT,
      { lowerValue: number; higherValue: number }
    >
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_NI_HIST_EXAMPLAR_CLICK,
      { entityId: string; entityName: string }
    >
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_BAR_CLICK,
      { impactCode: ImpactCategoryId; valence: Valence; lens: string }
    >
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_NI_DRILLDOWN_CLICK,
      { impactCode: ImpactCategoryId; valence: Valence }
    >
  | GenericGtmEventPayload<
      | CustomGtmEvent.IMPACT_PROFILE_NI_COMPARISON_ADD
      | CustomGtmEvent.IMPACT_PROFILE_NI_COMPARISON_REMOVE
      | CustomGtmEvent.IMPACT_PROFILE_NI_COMPARISON_CLICK,
      { comparisonEntityId: string; comparisonEntityName: string; comparisonEntityType: string }
    >
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_NI_GAUGES_SCREENER_CLICK, { gaugeType?: string }>
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_SDG_DRILLDOWN_OPEN,
      { sdgId: SdgId; valence: SdgAlignmentValence }
    >
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_SDG_UNIT_SELECT, { selectedUnit: SdgUnitsMode }>
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_PAI_DOWNLOAD, { metric?: PaiMetric }>
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_PAI_METRIC_SELECT, { metric: PaiMetric }>
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_PAI_SORT, { columnId: string; desc: boolean }>
  | GenericGtmEventPayload<
      | CustomGtmEvent.IMPACT_PROFILE_PAI_PAGE_FIRST
      | CustomGtmEvent.IMPACT_PROFILE_PAI_PAGE_PREVIOUS
      | CustomGtmEvent.IMPACT_PROFILE_PAI_PAGE_NEXT
      | CustomGtmEvent.IMPACT_PROFILE_PAI_PAGE_LAST,
      { destinationPage: number; lander?: boolean }
    >
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_EUT_TYPE_SELECT, { type: EuTaxonomyMetricTypes }>
  | GenericGtmEventPayload<
      CustomGtmEvent.IMPACT_PROFILE_EUT_ENTITY_CLICK,
      { entityId: string; entityName: string; entityType: string }
    >
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_EUT_ACT_CLICK, { activity: string }>
  | GenericGtmEventPayload<CustomGtmEvent.IMPACT_PROFILE_EUT_SORT, { columnId: string; desc: boolean }>
  | GenericGtmEventPayload<
      | CustomGtmEvent.IMPACT_PROFILE_EUT_PAGE_FIRST
      | CustomGtmEvent.IMPACT_PROFILE_EUT_PAGE_PREVIOUS
      | CustomGtmEvent.IMPACT_PROFILE_EUT_PAGE_NEXT
      | CustomGtmEvent.IMPACT_PROFILE_EUT_PAGE_LAST,
      { destinationPage: number; lander?: boolean }
    >;

type AssessmentSourceEventPayload =
  | GenericGtmEventPayload<CustomGtmEvent.ASSESSMENT_SOURCE_PAI, { release: string; variantId: string; metric: string }>
  | GenericGtmEventPayload<
      CustomGtmEvent.ASSESSMENT_SOURCE_EU_TAXONOMY,
      { release: string; variantId: string; objective: string; activityType: string; metric: string }
    >;

// TODO should this be removed?
export type PLGGtmEvent =
  | CustomGtmEvent.PLG_ACCOUNT_CREATION_STARTED
  | CustomGtmEvent.PLG_ACCOUNT_CREATION_COMPLETED
  | CustomGtmEvent.PLG_JOINED_ORGANISATION
  | CustomGtmEvent.PLG_CONFIRM_COMPANY_VIEWED
  | CustomGtmEvent.PLG_COMPANY_CONFIRMED_PRODUCT_FETCH_STARTED
  | CustomGtmEvent.PLG_PRODUCTS_FETCHED_SUCCESSFULLY
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_VIEWED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_DELETED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_ALL_PRODUCTS_DELETED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_ADDED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_EDITED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_SET_CONFIRMED
  | CustomGtmEvent.PLG_ASSESSMENT_TAKES_LONG_EMAIL_SENT // TODO: Backend
  | CustomGtmEvent.PLG_ASSESSMENT_COMPLETED_SUCCESFULLY
  | CustomGtmEvent.PLG_HOME_DASHBOARD_ACCESS
  | CustomGtmEvent.PLG_HOME_VIDEO_STARTED
  | CustomGtmEvent.PLG_HOME_VIDEO_COMPLETED
  | CustomGtmEvent.PLG_HOME_VIEW_RESULTS_CLICKED
  | CustomGtmEvent.PLG_HOME_DOCUMENTATION_TILE_CLICKED
  | CustomGtmEvent.PLG_HOME_NEWS_TILE_CLICKED
  | CustomGtmEvent.PLG_MATERIALITY_MATRIX_VIEWED
  | CustomGtmEvent.PLG_MATERIALITY_MATRIX_IMAGE_DOWNLOADED
  | CustomGtmEvent.PLG_MATERIAL_TOPICS_VIEWED
  | CustomGtmEvent.PLG_SMP_VIEWED // TODO
  | CustomGtmEvent.PLG_SMP_IMPACT_CARD_VIEWED // TODO
  | CustomGtmEvent.PLG_SMP_IMPACT_CARD_IMPACT_CALCULATION_TOOLTIP_VIEWED // TODO
  | CustomGtmEvent.PLG_SMP_IMPACT_CARD_PRODUCT_CALCULATION_TOOLTIP_VIEWED // TODO
  | CustomGtmEvent.PLG_SMP_FINANCIAL_MATERIALITY_TAB_CLICKED // TODO
  | CustomGtmEvent.PLG_SMP_RISKOPPORTUNITY_CARD_VIEWED // TODO
  | CustomGtmEvent.PLG_QUANTITATIVE_OVERVIEW_VIEWED
  | CustomGtmEvent.PLG_TAILORING_COMMIT_COPIED_RO
  | CustomGtmEvent.PLG_TAILORING_COMMIT_ADD_RO
  | CustomGtmEvent.PLG_TAILORING_COMMIT_COPIED_IMPACT
  | CustomGtmEvent.PLG_TAILORING_COMMIT_ADD_IMPACT
  | CustomGtmEvent.PLG_TAILORING_COMMIT_DISABLE_RO
  | CustomGtmEvent.PLG_TAILORING_COMMIT_DISABLE_IMPACT
  | CustomGtmEvent.PLG_TAILORING_COMMIT_EDIT_IRO // TODO
  | CustomGtmEvent.PLG_TAILORING_OWN_INSIGHTS_ACCESSED
  | CustomGtmEvent.PLG_TAILORING_ADJUSTED_IMPACT_THRESHOLD_COMMIT
  | CustomGtmEvent.PLG_TAILORING_ADJUSTED_FINANCIAL_THRESHOLD_COMMIT
  | CustomGtmEvent.PLG_TAILORING_CHANGE_LOG_VIEWED
  | CustomGtmEvent.PLG_TAILORING_DELETE_ALL_CHANGES_COMMIT
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_VIEWED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_DELETED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_ALL_PRODUCTS_DELETED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_ADDED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_EDITED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_ABANDONED_WITHOUT_RECOMPUTE // TODO
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_STARTED
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_COMPLETED_SUCCESSFULLY
  | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_FAILED
  | CustomGtmEvent.PLG_SIDE_NAVIGATION_FINANCIAL_EFFECTS_EXPANDED
  | CustomGtmEvent.PLG_SIDE_NAVIGATION_GEOGRAPHY_TOOLTIP_VIEWED // TODO
  | CustomGtmEvent.PLG_SIDE_NAVIGATION_DISCLOSURES_TOOLTIP_VIEWED // TODO
  | CustomGtmEvent.PLG_SIDE_NAVIGATION_FINANCIAL_STATEMENTS_TOOLTIP_VIEWED; // TODO

/**
 * Base payload for PLG events with additional data.
 * This interface provides a clearer structure by explicitly defining
 * the complete payload shape without relying on GenericGtmEventPayload's data property.
 */
interface PLGEventBasePayload<E extends CustomGtmEvent, T = unknown> {
  event: E;
  data: {
    userId?: number;
    organizationId?: number;
  } & T;
  contextOverride?: Partial<GoogleTagManagerEventContext>;
}

/**
 * Empty payload for PLG events that don't require additional data beyond user/org context.
 */
export interface PLGEmptyEventPayload<E extends CustomGtmEvent> {
  event: E;
  data?: {
    userId?: number;
    organizationId?: number;
  };
  contextOverride?: Partial<GoogleTagManagerEventContext>;
}

/**
 * PLG flow related events
 */
export type PLGEventPayload =
  | PLGEmptyEventPayload<
      | CustomGtmEvent.PLG_ACCOUNT_CREATION_STARTED
      | CustomGtmEvent.PLG_CONFIRM_COMPANY_VIEWED
      | CustomGtmEvent.PLG_JOINED_ORGANISATION
      | CustomGtmEvent.PLG_COMPANY_CONFIRMED_PRODUCT_FETCH_STARTED
      | CustomGtmEvent.PLG_PRODUCTS_FETCHED_SUCCESSFULLY
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_VIEWED
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_SET_CONFIRMED
      | CustomGtmEvent.PLG_ASSESSMENT_TAKES_LONG_EMAIL_SENT
      | CustomGtmEvent.PLG_ASSESSMENT_COMPLETED_SUCCESFULLY
      | CustomGtmEvent.PLG_HOME_DASHBOARD_ACCESS
      | CustomGtmEvent.PLG_HOME_VIDEO_STARTED
      | CustomGtmEvent.PLG_HOME_VIDEO_COMPLETED
      | CustomGtmEvent.PLG_HOME_VIEW_RESULTS_CLICKED
      | CustomGtmEvent.PLG_MATERIALITY_MATRIX_VIEWED
      | CustomGtmEvent.PLG_MATERIALITY_MATRIX_IMAGE_DOWNLOADED
      | CustomGtmEvent.PLG_MATERIAL_TOPICS_VIEWED
      | CustomGtmEvent.PLG_SMP_IMPACT_CARD_IMPACT_CALCULATION_TOOLTIP_VIEWED
      | CustomGtmEvent.PLG_SMP_IMPACT_CARD_PRODUCT_CALCULATION_TOOLTIP_VIEWED
      | CustomGtmEvent.PLG_SMP_RISKOPPORTUNITY_CARD_VIEWED
      | CustomGtmEvent.PLG_QUANTITATIVE_OVERVIEW_VIEWED
      | CustomGtmEvent.PLG_TAILORING_OWN_INSIGHTS_ACCESSED
      | CustomGtmEvent.PLG_TAILORING_CHANGE_LOG_VIEWED
      | CustomGtmEvent.PLG_TAILORING_DELETE_ALL_CHANGES_COMMIT
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_VIEWED
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_ABANDONED_WITHOUT_RECOMPUTE
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_STARTED
      | CustomGtmEvent.PLG_SIDE_NAVIGATION_FINANCIAL_EFFECTS_EXPANDED
      | CustomGtmEvent.PLG_SIDE_NAVIGATION_GEOGRAPHY_TOOLTIP_VIEWED
      | CustomGtmEvent.PLG_SIDE_NAVIGATION_DISCLOSURES_TOOLTIP_VIEWED
      | CustomGtmEvent.PLG_SIDE_NAVIGATION_FINANCIAL_STATEMENTS_TOOLTIP_VIEWED
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_ACCOUNT_CREATION_COMPLETED,
      {
        authenticationMethod?: string;
      }
    >
  | PLGEventBasePayload<
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_ADDED
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_DELETED
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_DELETED
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_ADDED,
      {
        productName?: string;
        productDescription?: string;
      }
    >
  | PLGEventBasePayload<
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_EDITED
      | CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_EDITED,
      {
        oldProductName?: string;
        newProductName?: string;
        productDescription?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_ALL_PRODUCTS_DELETED,
      {
        numberOfProducts?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_ADDED,
      {
        numberOfProducts?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_HOME_DOCUMENTATION_TILE_CLICKED,
      {
        documentationTitle?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_HOME_NEWS_TILE_CLICKED,
      {
        title?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_SMP_VIEWED,
      {
        matterName?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_SMP_IMPACT_CARD_VIEWED,
      {
        matterName?: string;
        impactName?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_SMP_FINANCIAL_MATERIALITY_TAB_CLICKED,
      {
        matterName?: string;
      }
    >
  | PLGEventBasePayload<
      | CustomGtmEvent.PLG_TAILORING_COMMIT_DISABLE_RO
      | CustomGtmEvent.PLG_TAILORING_COMMIT_DISABLE_IMPACT
      | CustomGtmEvent.PLG_TAILORING_COMMIT_COPIED_RO
      | CustomGtmEvent.PLG_TAILORING_COMMIT_ADD_RO
      | CustomGtmEvent.PLG_TAILORING_COMMIT_COPIED_IMPACT
      | CustomGtmEvent.PLG_TAILORING_COMMIT_ADD_IMPACT,
      {
        matterName?: string;
        typeOfIRO?: string;
        fieldValues?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_TAILORING_COMMIT_EDIT_IRO,
      {
        matterName?: string;
        typeOfIRO?: string;
        oldFieldValues?: string;
        newFieldValues?: string;
      }
    >
  | PLGEventBasePayload<
      | CustomGtmEvent.PLG_TAILORING_ADJUSTED_IMPACT_THRESHOLD_COMMIT
      | CustomGtmEvent.PLG_TAILORING_ADJUSTED_FINANCIAL_THRESHOLD_COMMIT,
      {
        previousThreshold?: string;
        newThreshold?: string;
        reasoning?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_ALL_PRODUCTS_DELETED,
      {
        numberOfProducts?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_COMPLETED_SUCCESSFULLY,
      {
        recomputationTime?: string;
      }
    >
  | PLGEventBasePayload<
      CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_FAILED,
      {
        errorCode?: string;
      }
    >
  | PLGEventBasePayload<
      // Agentic site-location input funnel (PLT-5470): proposed -> edited -> saved -> published.
      | CustomGtmEvent.PLG_SITE_LOCATIONS_PROPOSED
      | CustomGtmEvent.PLG_SITE_LOCATIONS_EDITED
      | CustomGtmEvent.PLG_SITE_LOCATIONS_SAVED
      | CustomGtmEvent.PLG_SITE_LOCATIONS_PUBLISHED,
      {
        companyId?: string;
        mode?: string;
        source?: string;
        siteCount?: number;
      }
    >;

export type EmailGtmEvent =
  | CustomGtmEvent.UA_FORM_EMAIL_TYPED
  | CustomGtmEvent.UA_CONTACT_FORM_SUBMIT
  | CustomGtmEvent.UA_DOWNLOAD_FORM_SUBMIT
  | CustomGtmEvent.UA_BOOK_DEMO_SUBMIT
  | CustomGtmEvent.UA_BOOK_INVESTOR_DEMO_SUBMIT
  | CustomGtmEvent.UA_WEBINAR_FORM_SUBMIT;

export type EmailEventPayload = GenericGtmEventPayload<
  EmailGtmEvent,
  {
    email?: string;
  }
>;

type FacadeEventPayload =
  | EmptyGtmEventPayload<
      | CustomGtmEvent.UA_BOOK_INVESTOR_DEMO_CLICK
      | CustomGtmEvent.UA_BOOK_INVESTOR_DEMO_SUBMIT
      | CustomGtmEvent.UA_CONTACT_CLICK
      | CustomGtmEvent.UA_CONTACT_FORM_SUBMIT
      | CustomGtmEvent.UA_CONTACT_FORM_INTERACTED
      | CustomGtmEvent.UA_NON_LEAD_CONTACT_CLICK
      | CustomGtmEvent.UA_NON_LEAD_CONTACT_FORM_SUBMIT
      | CustomGtmEvent.UA_NON_LEAD_CONTACT_FORM_INTERACTED
      | CustomGtmEvent.UA_BOOK_DEMO_CLICK
      | CustomGtmEvent.UA_BOOK_DEMO_SUBMIT
      | CustomGtmEvent.UA_BOOK_DEMO_FORM_INTERACTED
      | CustomGtmEvent.UA_DOWNLOAD_FORM_CLICK
      | CustomGtmEvent.UA_DOWNLOAD_FORM_SUBMIT
      | CustomGtmEvent.UA_DOWNLOAD_FORM_INTERACTED
      | CustomGtmEvent.UA_WEBINAR_CLICK
      | CustomGtmEvent.UA_WEBINAR_FORM_SUBMIT
      | CustomGtmEvent.UA_WEBINAR_FORM_INTERACTED
      | CustomGtmEvent.UA_HEADER_PLATFORM_LINK_CLICK
      | CustomGtmEvent.UA_FORM_PHONE_REVEAL_CLICKED
      | CustomGtmEvent.UA_FORM_EMAIL_ADDRESS_CLICKED
      | CustomGtmEvent.UA_INLINE_FORM_VISIBLE
    >
  | GenericGtmEventPayload<
      CustomGtmEvent.UA_BOOK_COMPANY_DEMO_CLICK | CustomGtmEvent.UA_BOOK_COMPANY_DEMO_SUBMIT,
      {
        tier?: string;
      }
    >
  | EmailEventPayload;

export interface SerializedPayload {
  event: CustomGtmEvent;
  data?: string;
  context?: string;
  debug_mode?: boolean;
}

export function isConversionEventType(eventName: CustomGtmEvent) {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (eventName) {
    case CustomGtmEvent.UA_BOOK_COMPANY_DEMO_SUBMIT:
    case CustomGtmEvent.UA_BOOK_INVESTOR_DEMO_SUBMIT:
    case CustomGtmEvent.UA_BOOK_DEMO_SUBMIT:
    case CustomGtmEvent.UA_DOWNLOAD_FORM_SUBMIT:
    case CustomGtmEvent.UA_WEBINAR_FORM_SUBMIT:
    case CustomGtmEvent.UA_CONTACT_FORM_SUBMIT:
    case CustomGtmEvent.UA_FORM_PHONE_REVEAL_CLICKED:
    case CustomGtmEvent.UA_FORM_EMAIL_ADDRESS_CLICKED:
      return true;
    default:
      return false;
  }
}
